/**
 * What a room looks like in a list: a tile for a channel, a face for a person.
 *
 * The two have to occupy the same square, or a mixed list — the overview, where
 * channels and direct messages sit in neighbouring columns — reads as two
 * lists that happen to be adjacent rather than one set of conversations.
 */

import type { ReactElement } from 'react'

import type { Room } from '@/api/types'
import { Avatar, HashIcon, LockIcon } from '@/components/ui'
import styles from './RoomGlyph.module.css'

export interface RoomGlyphProps {
  room: Room
  /** The name the row shows, which is what a direct message's initials come from. */
  label: string
  size?: 'sm' | 'md'
}

export function RoomGlyph({ room, label, size = 'sm' }: RoomGlyphProps): ReactElement {
  if (room.kind !== 'channel') {
    return <Avatar name={label} id={room.id} size={size} decorative />
  }

  const closed = room.visibility === 'private'
  return (
    <span className={`${styles.tile} ${styles[size]}`} aria-hidden="true">
      {closed ? <LockIcon size={size === 'md' ? 15 : 13} /> : <HashIcon size={size === 'md' ? 16 : 14} />}
    </span>
  )
}
